import { useState, useContext, useEffect, useCallback } from 'react';
import DepartmentTable from '../components/departments/DepartmentTable';
import {
  Box,
  Container,
  Modal,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getCompanyDepartment, getCompanies, getAllDepartments, addDepartment, editDepartment, deleteAllDepartment, deleteDepartment } from '../apis/axios';
import AuthContext from '../AuthContext';

function Departments() {
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [company, setCompany] = useState('');
  const [tableInstance, setTableInstance] = useState(null);
  const [update, setUpdate] = useState(false);

  const handleUpdate = () => {
    setUpdate(!update);
  };

  const fetchCompanies = async () => {
    try {
      const response = await getCompanies();
      if (response && Array.isArray(response.data)) {
        setCompanies(response.data);
      } else {
        console.error('Invalid response format for companies:', response.data);
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [update]);

  const fetchDepartments = useCallback(async () => {
    try {
      if(auth.userType==='superadmin'){
         const response = await getAllDepartments(company);
      if (response) {
        setDepartments(response.data);
      } else {
        console.error('Invalid response format for departments:', response.data);
        setDepartments([]);
      }
      }
     else{
      const payload={company_id: company}
       const response = await getCompanyDepartment(payload);
      if (response) {
        setDepartments(response.data);
      } else {
        console.error('Invalid response format for departments:', response.data);
        setDepartments([]);
      }
     }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  }, [company]);

  useEffect(() => {
    if (auth.admin) {
      setCompany(auth.admin);
    }
  }, [auth.admin, update ,auth.userType]);

  useEffect(() => {
      fetchDepartments();
  }, [company, fetchDepartments, update]);

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    companyId: '',
    companyName: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  // Function to export data as CSV
  const exportToCSV = (data, filename) => {
    const headers = ['Department', 'Company'];
    const rows = data.map(item => [item.name, item.company_name]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    if (field === 'companyId') {
      const selectedCompany = companies.find(company => company.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        companyId: value,
        companyName: selectedCompany ? selectedCompany.name : '',
      }));
      setCompany(value);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Handle form submission for adding a new department
  const handleAddDepartment = async () => {
    const newDepartment = {
      name: formData.name,
      company_id: company,
    };
    try {
      const res = await addDepartment(newDepartment);
      if (res) {
        handleUpdate();
      }
    } catch (error) {
      console.error('Error adding department:', error);
    }
    closeModal();
  };

  // Handle form submission for updating an existing department
  const handleUpdateDepartment = async () => {
    const newDepartment = {
      id: formData.id,
      name: formData.name,
      company_id: company,
    };
    try {
      const res = await editDepartment(newDepartment);
      if (res) {
        handleUpdate();
      }
    } catch (error) {
      console.error('Error updating department:', error);
    }
    closeModal();
  };

  // Handle edit department
  const handleEditDepartment = (department) => {
    setFormData({
      id: department.id,
      name: department.name,
      companyId: department.company_id,
    });
    setCompany(department.company_id);
    setIsEditing(true);
    setOpenModal(true);
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (!tableInstance) return;

    let departmentIds = [];

    if (action === 'deleteAll') {
      departmentIds = departments.map(dep => dep.id);
    } else if (action === 'deleteSelected') {
      const selectedRows = tableInstance.getSelectedRowModel().rows;
      departmentIds = selectedRows.map(row => row.original.id);
    }

    if (departmentIds.length === 0) return;

    try {
      await deleteAllDepartment({ department_ids: departmentIds });
      handleUpdate();
      if (action === 'deleteSelected') {
        tableInstance.resetRowSelection();
      }
    } catch (error) {
      console.error('Error during bulk deletion:', error);
    }
  };

  const handleDelete = async (data) => {
    try {
      const res = await deleteDepartment({ department_id: data });
      if (res) {
        handleUpdate();
      }
    } catch (error) {
      console.error('Error deleting department:', error);
    }
  };

  // Close modal and reset form
  const closeModal = () => {
    setOpenModal(false);
    setIsEditing(false);
    setFormData({
      id: null,
      name: '',
      companyId: '',
      companyName: '',
    });
  };

  // Ripple effect handler
  const handleRippleEffect = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
    ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
    ripple.classList.add('export-button-ripple');
    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  return (
    <Box display="flex" sx={{ maxWidth: 'none', justifyContent: 'center' }}>
      <Box flexGrow={1} ml={{ sm: 0 }} sx={{ maxWidth: 'none' }}>
        <Container
          sx={{
            maxWidth: 'none !important',
            overflowX: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
            
          }}
        >
          <Box sx={{ margin: '0 auto', width: '75.5vw', maxWidth: '75.5vw'}}>
            {/* Header and Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4,   flexDirection: { xs: 'column', md: 'row' }, // 👈 Mobile = column, Desktop = row
    gap: { xs: 2, md: 0 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Department
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    exportToCSV(departments, 'departments.csv');
                  }}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 500,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                  }}
                >
                  Export
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    handleRippleEffect(e);
                    setOpenModal(true);
                    setIsEditing(false);
                  }}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 500,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                  }}
                >
                  Add Departments
                </Button>
              </Box>
            </Box>

            {/* Department Table */}
            <Box>
              <DepartmentTable
                data={departments}
                setDepartments={setDepartments}
                onEdit={handleEditDepartment}
                onDelete={handleDelete}
                setTableInstance={setTableInstance}
                userType={auth.userType}
              />
            </Box>
          </Box>

          {/* Add/Edit Department Modal */}
          <Modal
            open={openModal}
            onClose={closeModal}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                bgcolor: theme.palette.background.paper,
                p: 4,
                borderRadius: 2,
                width: '50vw',
                maxWidth: 900,
                maxHeight: { xs: '90vh', sm: 'auto' },
                overflowY: { xs: 'auto', sm: 'visible' },
                boxShadow: theme.shadows[10],
              }}
            >
              {/* Modal Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {isEditing ? 'Edit Department' : 'Add Department'}
                </Typography>
                <IconButton onClick={closeModal}>
                  <CloseIcon sx={{ color: theme.palette.error.main }} />
                </IconButton>
              </Box>

              {/* Department Information Section */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 2,
                }}
              >
                Department Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                {/* Department Name */}
                <TextField
                  label="Department Name*"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., IT Department"
                  fullWidth
                 
                />
                {/* Company */}
                {auth.userType === 'superadmin' && (
                  <FormControl fullWidth>
                    <InputLabel
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 600,
                      }}
                    >
                      Company*
                    </InputLabel>
                    <Select
                      value={company}
                      onChange={(e) => handleInputChange('companyId', e.target.value)}
                      label="Company*"
              
                    >
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ flex: 1 }}></Box>
              </Box>

              {/* Add/Update Department Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    handleRippleEffect(e);
                    isEditing ? handleUpdateDepartment() : handleAddDepartment();
                  }}
                  disabled={!formData.name || !company}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                    '&:disabled': {
                      bgcolor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  {isEditing ? 'Update Department' : 'Add Department'}
                </Button>
              </Box>
            </Box>
          </Modal>
        </Container>
      </Box>

  
    </Box>
  );
}

export default Departments;